import { X } from "lucide-react";
import { useState } from "react";

type TeacherProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  teacherName: string;
  schoolName: string;
  email: string;
  avatarSrc: string;
  onUpdatePassword: (payload: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => { ok: boolean; message: string };
  onLogout: () => void;
  classSectionMap: Array<{
    grade: number;
    sections: string[];
  }>;
};

const TeacherProfileModal = ({
  isOpen,
  onClose,
  teacherName,
  schoolName,
  email,
  avatarSrc,
  onUpdatePassword,
  onLogout,
  classSectionMap,
}: TeacherProfileModalProps) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  if (!isOpen) return null;

  const handleUpdatePassword = () => {
    const result = onUpdatePassword({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    setFeedback({
      type: result.ok ? "success" : "error",
      message: result.message,
    });

    if (result.ok) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#2f1f4b]/25 p-4 backdrop-blur-[2px] md:p-6">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-label="Close profile"
      />

      <div className="relative mx-auto my-2 w-full max-w-[620px] rounded-2xl border border-[#efdff8] bg-[linear-gradient(180deg,#fff9ff_0%,#ffeefe_100%)] p-5 shadow-2xl shadow-fuchsia-200/60 md:my-6 md:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1.5 text-[#b184cc] transition hover:bg-white/80 hover:text-[#6f549f]"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4 border-b border-[#ead8f6] pb-4">
          <div className="h-14 w-14 overflow-hidden rounded-full ring-2 ring-white shadow-lg">
            <img
              src={avatarSrc}
              alt="Teacher avatar"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-extrabold text-[#5c427f]">
              {teacherName}
            </h3>
            <p className="truncate text-xs font-medium text-[#8f78b3]">
              {schoolName}
            </p>
            <p className="truncate text-xs font-medium text-[#a086c0]">
              {email}
            </p>
          </div>
        </div>

        <div className="pt-4">
          <h4 className="mb-2 text-sm font-bold text-[#6f549f]">Classes & Sections Teaching</h4>
          <div className="space-y-2">
            {classSectionMap.map((entry) => (
              <div
                key={entry.grade}
                className="flex flex-wrap items-center gap-2 rounded-xl border border-[#ebdbf7] bg-white/75 px-3 py-2"
              >
                <span className="text-xs font-bold text-[#6a4f95]">Grade {entry.grade}</span>
                {entry.sections.map((section) => (
                  <span
                    key={`${entry.grade}-${section}`}
                    className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#d9c1f7] px-2 text-[10px] font-extrabold text-[#5d3f8f]"
                  >
                    {section}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 border-t border-[#ead8f6] pt-4">
          <h4 className="mb-3 text-sm font-bold text-[#6f549f]">Account Settings</h4>
          <div className="space-y-2.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#8f78b3]">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (feedback) setFeedback(null);
                }}
                placeholder="Enter current password"
                className="w-full rounded-lg border border-[#ead7f7] bg-white px-3 py-2 text-sm text-[#6f549f] outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#8f78b3]">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (feedback) setFeedback(null);
                }}
                placeholder="Enter new password"
                className="w-full rounded-lg border border-[#ead7f7] bg-white px-3 py-2 text-sm text-[#6f549f] outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#8f78b3]">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (feedback) setFeedback(null);
                }}
                placeholder="Confirm new password"
                className="w-full rounded-lg border border-[#ead7f7] bg-white px-3 py-2 text-sm text-[#6f549f] outline-none"
              />
            </div>
          </div>

          {feedback && (
            <p
              className={`mt-3 rounded-lg px-3 py-2 text-xs font-semibold ${
                feedback.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {feedback.message}
            </p>
          )}

          <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
            <button
              type="button"
              onClick={handleUpdatePassword}
              className="flex-1 rounded-lg bg-gradient-to-r from-[#4cc681] to-[#34b574] px-4 py-2 text-sm font-bold text-white shadow-md shadow-green-200"
            >
              Update Password
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="flex-1 rounded-lg bg-gradient-to-r from-[#ff6aa3] to-[#ff4f8a] px-4 py-2 text-sm font-bold text-white shadow-md shadow-pink-200"
            >
              Log Out of Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfileModal;
